<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { inject, ref, onBeforeMount, computed } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper, useResponsive } from "@core/composables"
import type { ColumnConfig } from "@types"
import type { Services } from "@services"
import type { CompanyUser } from "@core/services/user.service"

// ================================
// TYPES & INTERFACES
// ================================
type FilterOption = {
    label: string
    value: "all" | "pro" | "particulier"
}

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Invités",
            clients_load_error:
                "Nous rencontrons un problème pour afficher vos invités pour le moment. Veuillez réessayer plus tard.",
            placeholder: "Rechercher un invité...",
            empty: "Aucun invité trouvé",
            no_client: (ctx: { search?: string }) =>
                ctx.search
                    ? `Aucun invité trouvé pour "${ctx.search}"`
                    : "Aucun invité trouvé",
            total: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} invité${Number(ctx.nb) > 1 ? "s" : ""}`,
            professional: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} professionnel${Number(ctx.nb) > 1 ? "s" : ""}`,
            individual: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} particulier${Number(ctx.nb) > 1 ? "s" : ""}`,
            filter: {
                all: "Tous",
                professional: "Professionnels",
                individual: "Particuliers",
            },
            column: {
                name: "Nom",
                contact: "Contact",
                type: "Type",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================

const { isMobile } = useResponsive()

/**
 * Injection des services pour récupérer les utilisateurs entreprise
 */
const { $getCompanyUsers } = inject<Services>("services")!

/**
 * Chargement des invités via le service
 */
const {
    fetch: fetchClients,
    isFetching,
    hasError,
} = useFetchWrapper(async () => {
    if (!$getCompanyUsers) {
        throw new Error("Service $getCompanyUsers non disponible")
    }
    clients.value = await $getCompanyUsers(["client"])
})

// ================================
// STATE
// ================================

/**
 * Liste des invités de l'entreprise
 */
const clients = ref<CompanyUser[]>([])

/**
 * Valeur du filtre en cours (all / pro / particulier)
 */
const filterValue = ref<"all" | "pro" | "particulier">("all")

/**
 * Valeur de la recherche globale (filtre côté client)
 */
const filters = ref({
    global: { value: null as string | null },
})

// ================================
// COMPUTED
// ================================

/**
 * Liste des invités normalisée (garantit un tableau)
 */
const getNormalizedClients = computed(() => {
    if (!clients.value || !Array.isArray(clients.value)) {
        return []
    }
    return clients.value
})

/**
 * Nombre total d'invités
 */
const getClientsCount = computed(() => getNormalizedClients.value?.length ?? 0)

/**
 * Nombre d'invités professionnels
 */
const getNbPro = computed(
    () =>
        getNormalizedClients.value.filter((item) => !!item.company?.name).length
)

/**
 * Nombre d'invités particuliers
 */
const getNbParticulier = computed(
    () =>
        getNormalizedClients.value.filter((item) => !item.company?.name).length
)

/**
 * Options de filtre par type (avec nombre total à côté du label)
 */
const getFilterOptions = computed<FilterOption[]>(() => [
    {
        label: `${t("filter.all")} (${getClientsCount.value})`,
        value: "all",
    },
    {
        label: `${t("filter.professional")} (${getNbPro.value})`,
        value: "pro",
    },
    {
        label: `${t("filter.individual")} (${getNbParticulier.value})`,
        value: "particulier",
    },
])

/**
 * Liste des invités filtrés et fusionnés
 * - Filtre par type (all/pro/particulier)
 * - Filtre par recherche (début du nom, prénom ou email)
 * - Fusionne les doublons (même _id) en concaténant les noms d'entreprises
 */
const getClientsFiltered = computed(() => {
    let filteredClients = [...getNormalizedClients.value]

    if (filterValue.value === "pro") {
        filteredClients = getNormalizedClients.value.filter(
            (item) => !!item.company?.name
        )
    } else if (filterValue.value === "particulier") {
        filteredClients = getNormalizedClients.value.filter(
            (item) => !item.company?.name
        )
    }

    const searchValue = filters.value.global.value
    if (searchValue && typeof searchValue === "string") {
        const searchLower = searchValue.toLowerCase().trim()
        if (searchLower) {
            filteredClients = filteredClients.filter((item) => {
                const firstname = String(item.firstname || "").toLowerCase()
                const lastname = String(item.lastname || "").toLowerCase()
                const username = String(item.username || "").toLowerCase()
                return (
                    firstname.startsWith(searchLower) ||
                    lastname.startsWith(searchLower) ||
                    username.startsWith(searchLower)
                )
            })
        }
    }

    return filteredClients.reduce((acc, item, itemIndex) => {
        const existingIndex = acc.findIndex((i) => i._id === item._id)
        if (existingIndex !== -1 && itemIndex !== existingIndex) {
            const existing = acc[existingIndex]
            acc[existingIndex] = {
                ...existing,
                company: existing.company
                    ? {
                          ...existing.company,
                          name: `${existing.company.name}${
                              item.company?.name ? ", " + item.company.name : ""
                          }`,
                      }
                    : item.company,
            }
        } else {
            acc.push(item)
        }
        return acc
    }, [] as CompanyUser[])
})

/**
 * Message affiché lorsque la liste est vide
 */
const getEmptyMessage = computed(() => {
    const searchValue = filters.value.global.value
    if (searchValue) {
        return t("no_client", { search: searchValue })
    }
    return t("empty")
})

/**
 * Configuration des colonnes pour BaseTable
 */
const getClientColumns = computed<ColumnConfig[]>(() => [
    {
        field: "name",
        header: t("column.name"),
        sortable: false,
        style: { width: "24rem" },
    },
    {
        field: "contact",
        header: t("column.contact"),
        sortable: false,
        style: { maxWidth: "20rem" },
    },
    { field: "type", header: t("column.type"), sortable: false },
])

// ================================
// HELPERS
// ================================

/**
 * Met en majuscule la première lettre d'une chaîne
 */
function capitalize(str: string): string {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Retourne les initiales de l'invité pour l'avatar
 */
function getInitials(client: CompanyUser): string {
    const firstname = client?.firstname || ""
    const lastname = client?.lastname || ""
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase()
}

/**
 * Retourne le nom complet de l'invité
 */
function getFullName(client: CompanyUser): string {
    const firstname = capitalize(client?.firstname || "")
    const lastname = capitalize(client?.lastname || "")
    return `${firstname} ${lastname}`.trim()
}

/**
 * Retourne le label du type d'invité : Professionnel ou Particulier
 */
function getClientTypeLabel(client: CompanyUser): string {
    return client?.company?.name ? "Professionnel" : "Particulier"
}

// ================================
// LIFECYCLE
// ================================

/**
 * Charge les invités au montage du composant
 */
onBeforeMount(() => {
    fetchClients()
})
</script>

<template>
    <BaseTitledCard class="company-clients">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <!-- Message d'erreur -->
                <BaseMessage v-if="hasError" severity="error">
                    {{ t("clients_load_error") }}
                </BaseMessage>
                <!-- Skeleton pendant le chargement -->
                <BaseSkeleton
                    v-else-if="isFetching"
                    width="100%"
                    height="30vh"
                    class="mx-auto"
                />
                <!-- Liste des invités -->
                <div v-else class="company-clients-list flex flex-col gap-4">
                    <!-- Header : recherche et filtres -->
                    <div
                        class="flex flex-wrap items-center justify-between gap-4 mb-4"
                    >
                        <BaseInputWrapper
                            :label="t('placeholder')"
                            input-id="input-search-clients"
                            class="md:max-w-[37.5rem] w-full min-w-[18rem]"
                        >
                            <BaseInputText
                                v-model="filters.global.value"
                                input-id="input-search-clients"
                            />
                        </BaseInputWrapper>
                        <BaseSelect
                            v-if="isMobile"
                            v-model="filterValue"
                            :options="getFilterOptions"
                            option-label="label"
                            option-value="value"
                            class="w-full"
                        />
                        <BaseSelectButton
                            v-else
                            v-model="filterValue"
                            :options="getFilterOptions"
                            option-label="label"
                            option-value="value"
                        />
                    </div>

                    <!-- Tableau des invités -->
                    <BaseTable
                        data-key="_id"
                        :columns="getClientColumns"
                        :value="getClientsFiltered"
                        paginator
                        :rows="10"
                        :rows-per-page-options="[10, 25, 50]"
                        :empty-message="getEmptyMessage"
                        size="small"
                    >
                        <template #cell_name="{ data }">
                            <div class="flex items-center gap-4 min-w-0">
                                <figure
                                    v-if="!data?.img"
                                    class="flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-medium text-sm uppercase w-8 h-8"
                                >
                                    {{ getInitials(data) }}
                                </figure>
                                <img
                                    v-else
                                    :src="data.img"
                                    :alt="getFullName(data)"
                                    class="flex-shrink-0 rounded-full object-cover w-8 h-8"
                                />
                                <p
                                    class="capitalize min-w-0 truncate"
                                    :title="getFullName(data)"
                                >
                                    {{ getFullName(data) }}
                                    <span
                                        v-if="data?.company?.name"
                                        class="block text-xs uppercase text-gray-600 mt-0.5"
                                        :title="data.company.name"
                                    >
                                        {{ data.company.name }}
                                    </span>
                                </p>
                            </div>
                        </template>
                        <template #cell_contact="{ data }">
                            <p
                                class="lowercase truncate"
                                :title="data?.username"
                            >
                                {{ data?.username }}
                            </p>
                        </template>
                        <template #cell_type="{ data }">
                            <p :title="getClientTypeLabel(data)">
                                {{ getClientTypeLabel(data) }}
                            </p>
                        </template>
                        <template #empty>
                            <div class="p-8 text-center text-gray-600">
                                {{ getEmptyMessage }}
                            </div>
                        </template>
                    </BaseTable>
                </div>
            </div>
        </template>
    </BaseTitledCard>
</template>
